import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../services/api.service';
import { ShiftReportData, ShiftReportRequest } from './models/shift-report.model';
import {
  CashReportData,
  CashReportEmployee,
  CashReportMatchStatus,
  CashReportRequest,
  CashReportRow,
} from './models/cash-report.model';
import {
  ScheduledReport,
  ScheduledReportDraft,
  ScheduledReportStatus,
} from './models/scheduled-report.model';
import { formatYmdToPretty } from '../employee/components/cash-reconciliation/helpers/date.helper';
import {
  GeneralSalesFilterOptions,
  GeneralSalesFilterOptionsRequest,
  GeneralSalesReportData,
  GeneralSalesReportRequest,
} from './models/general-sales-report.model';

export interface ScheduledReportWritePayload {
  report_type: ScheduledReportDraft['report_type'];
  location_ids: string[];
  employee_ids: string[];
  frequency_kind: ScheduledReportDraft['frequency_kind'];
  weekday?: string | null;
  month_day?: number | null;
  delivery_time?: string | null;
  individual_reports: boolean;
  format: ScheduledReportDraft['format'];
  start_date: string;
  recipient_emails: string[];
}

@Injectable({ providedIn: 'root' })
export class ReportsService {
  private readonly api = inject(ApiService);

  /**
   * General Sales Report (CWS-1230).
   *
   * POST analytics/reports/general-sales — the report is a body-shaped query
   * (multi-location array + comparison flag), not a cacheable resource.
   *
   * Location and role permissions are resolved server-side; anything the caller
   * is not entitled to is dropped there, so the response is already filtered.
   */
  getGeneralSalesReport(body: GeneralSalesReportRequest): Observable<GeneralSalesReportData> {
    return this.api.post<GeneralSalesReportData>('analytics/reports/general-sales', body).pipe(
      map(response => {
        if (!response?.status || !response.data) {
          throw new Error(response?.message || 'Failed to load general sales report');
        }
        return response.data;
      })
    );
  }

  /**
   * Options for the report's filter controls, scoped to the selected period and
   * locations so a control never offers a value that returns nothing.
   */
  getGeneralSalesFilterOptions(
    body: GeneralSalesFilterOptionsRequest
  ): Observable<GeneralSalesFilterOptions> {
    return this.api
      .post<GeneralSalesFilterOptions>('analytics/reports/general-sales/filter-options', body)
      .pipe(
        map(response => {
          if (!response?.status || !response.data) {
            throw new Error(response?.message || 'Failed to load filter options');
          }
          return response.data;
        })
      );
  }

  /**
   * CSV export (PRD §22). The server runs the same scope and permission
   * resolution as the report itself, so the file matches what is on screen.
   */
  downloadGeneralSalesCsv(body: GeneralSalesReportRequest): Observable<Blob> {
    return this.api.postBlob('analytics/reports/general-sales/export/csv', body);
  }

  /**
   * Excel export. CSV cannot carry formatting of any kind, so this is the one to
   * hand someone who will read the numbers rather than feed them to a script.
   */
  downloadGeneralSalesXlsx(body: GeneralSalesReportRequest): Observable<Blob> {
    return this.api.postBlob('analytics/reports/general-sales/export/xlsx', body);
  }

  /** PDF export, formatted for sharing outside the product. */
  downloadGeneralSalesPdf(body: GeneralSalesReportRequest): Observable<Blob> {
    return this.api.postBlob('analytics/reports/general-sales/export/pdf', body);
  }

  getShiftReport(body: ShiftReportRequest): Observable<ShiftReportData> {
    return this.api.post<ShiftReportData>('employee/shift-report', body).pipe(
      map(response => {
        if (!response?.status || !response.data) {
          throw new Error(response?.message || 'Failed to load shift report');
        }
        return response.data;
      })
    );
  }

  /**
   * Download Shift report as XLSX.
   * POST employee/shift-report/download/xlsx
   */
  downloadShiftReportXlsx(body: ShiftReportRequest): Observable<Blob> {
    const payload: ShiftReportRequest = { date: body.date };
    if (body.location_ids?.length) {
      payload.location_ids = body.location_ids;
    }
    if (body.employee_ids?.length) {
      payload.employee_ids = body.employee_ids;
    }
    return this.api.postBlob('employee/shift-report/download/xlsx', payload);
  }

  /**
   * Download Shift report as PDF.
   * POST employee/shift-report/download
   */
  downloadShiftReportPdf(body: ShiftReportRequest): Observable<Blob> {
    const payload: ShiftReportRequest = { date: body.date };
    if (body.location_ids?.length) {
      payload.location_ids = body.location_ids;
    }
    if (body.employee_ids?.length) {
      payload.employee_ids = body.employee_ids;
    }
    return this.api.postBlob('employee/shift-report/download', payload);
  }

  /**
   * Reuses Cash Reconciliation report API:
   * POST employee/cash-reconciliation/report
   * Body: { startDate, endDate, employee_ids? } (max 31 days)
   */
  getCashReport(body: CashReportRequest): Observable<CashReportData> {
    const payload: CashReportRequest = {
      startDate: body.startDate,
      endDate: body.endDate,
    };
    if (body.employee_ids?.length) {
      payload.employee_ids = body.employee_ids;
    }

    return this.api.post<any[]>('employee/cash-reconciliation/report', payload).pipe(
      map(response => {
        if (!response?.status) {
          throw new Error(response?.message || 'Failed to load cash report');
        }
        return this.normalizeCashReport(
          Array.isArray(response.data) ? response.data : [],
          body.startDate,
          body.endDate
        );
      })
    );
  }

  /**
   * Download Cash Reconciliation report as XLSX.
   * POST employee/cash-reconciliation/report/download/xlsx
   */
  downloadCashReportXlsx(body: CashReportRequest): Observable<Blob> {
    const payload: CashReportRequest = {
      startDate: body.startDate,
      endDate: body.endDate,
    };
    if (body.employee_ids?.length) {
      payload.employee_ids = body.employee_ids;
    }
    return this.api.postBlob('employee/cash-reconciliation/report/download/xlsx', payload);
  }

  /**
   * Download Cash Reconciliation report as PDF.
   * POST employee/cash-reconciliation/report/download
   */
  downloadCashReportPdf(body: CashReportRequest): Observable<Blob> {
    const payload: CashReportRequest = {
      startDate: body.startDate,
      endDate: body.endDate,
    };
    if (body.employee_ids?.length) {
      payload.employee_ids = body.employee_ids;
    }
    return this.api.postBlob('employee/cash-reconciliation/report/download', payload);
  }

  listScheduledReports(): Observable<ScheduledReport[]> {
    return this.api.get<ScheduledReport[]>('employee/scheduled-reports').pipe(
      map(response => {
        if (!response?.status) {
          throw new Error(response?.message || 'Failed to load scheduled reports');
        }
        return Array.isArray(response.data) ? response.data : [];
      })
    );
  }

  createScheduledReport(payload: ScheduledReportWritePayload): Observable<ScheduledReport> {
    return this.api.post<ScheduledReport>('employee/scheduled-reports', payload).pipe(
      map(response => {
        if (!response?.status || !response.data) {
          throw new Error(response?.message || 'Failed to create schedule');
        }
        return response.data;
      })
    );
  }

  updateScheduledReport(
    id: string,
    payload: ScheduledReportWritePayload
  ): Observable<ScheduledReport> {
    return this.api.put<ScheduledReport>(`employee/scheduled-reports/${id}`, payload).pipe(
      map(response => {
        if (!response?.status || !response.data) {
          throw new Error(response?.message || 'Failed to update schedule');
        }
        return response.data;
      })
    );
  }

  setScheduledReportStatus(id: string, status: ScheduledReportStatus): Observable<ScheduledReport> {
    return this.api
      .patch<ScheduledReport>(`employee/scheduled-reports/${id}/status`, { status })
      .pipe(
        map(response => {
          if (!response?.status || !response.data) {
            throw new Error(response?.message || 'Failed to update schedule status');
          }
          return response.data;
        })
      );
  }

  deleteScheduledReport(id: string): Observable<{ id: string }> {
    return this.api.delete<{ id: string }>(`employee/scheduled-reports/${id}`).pipe(
      map(response => {
        if (!response?.status) {
          throw new Error(response?.message || 'Failed to delete schedule');
        }
        return response.data || { id };
      })
    );
  }

  draftToWritePayload(draft: ScheduledReportDraft): ScheduledReportWritePayload {
    const startDate = toYmd(draft.start_date) || toYmd(new Date()) || '';
    const recipientEmails = draft.recipient_emails.map(e => e.trim()).filter(Boolean);
    const frequencyKind = draft.frequency_kind;

    const payload: ScheduledReportWritePayload = {
      report_type: draft.report_type,
      location_ids: [...draft.location_ids],
      employee_ids: [...draft.employee_ids],
      frequency_kind: frequencyKind,
      individual_reports: frequencyKind !== 'shift_end' ? !!draft.individual_reports : false,
      format: draft.format,
      start_date: startDate,
      recipient_emails: recipientEmails,
    };

    if (frequencyKind !== 'shift_end') {
      payload.delivery_time = draft.delivery_time;
      if (frequencyKind === 'weekly') {
        payload.weekday = draft.weekday;
      }
      if (frequencyKind === 'monthly') {
        payload.month_day = draft.month_day;
      }
    }

    return payload;
  }

  private normalizeCashReport(apiData: any[], startDate: string, endDate: string): CashReportData {
    const employees: CashReportEmployee[] = apiData.map(empRaw => {
      const emp = (empRaw || {}) as Record<string, any>;
      const employeeId = String(emp['employee_id'] ?? '');
      const name = String(emp['attendant'] ?? 'Employee');
      const rawRows = Array.isArray(emp['rows']) ? emp['rows'] : [];

      const rows: CashReportRow[] = rawRows.map(rowRaw => {
        const row = (rowRaw || {}) as Record<string, any>;
        const matchStatus = normalizeMatchStatus(row['match_status']);
        const isMatchedOrUnmatched = matchStatus === 'matched' || matchStatus === 'unmatched';
        const deltaRaw = toNum(row['delta']);

        return {
          id: String(row['id'] ?? `${employeeId}-${row['date'] ?? ''}`),
          employee_id: employeeId,
          date: String(row['date'] ?? row['entry_date'] ?? ''),
          attendant: String(row['attendant'] ?? name),
          status: String(row['match_status'] ?? row['status'] ?? ''),
          status_key: matchStatus,
          shift: formatShift(row['shift']),
          location: String(row['location'] ?? '—'),
          opening_balance: toNum(row['opening_balance']),
          extra_cash_total: toNum(row['extra_cash_total']),
          cash_collected: toNum(row['payment_received']),
          expected_closing: toNum(row['expected_closing']),
          closing_balance: toNum(row['closing_balance']),
          delta: isMatchedOrUnmatched ? deltaRaw : null,
          match_status: matchStatus,
        };
      });

      return {
        employee_id: employeeId || name,
        name,
        rows,
        totals: buildTotals(rows),
      };
    });

    employees.sort((a, b) => a.name.localeCompare(b.name));

    const allRows = employees.flatMap(emp => emp.rows);
    const overall = buildTotals(allRows);

    return {
      employees,
      date_range_label: `${formatYmdToPretty(startDate)} - ${formatYmdToPretty(endDate)}`,
      shift_count: allRows.length,
      employee_count: employees.length,
      total_cash_collected: overall.total_cash_collected,
      total_expected_closing: overall.total_expected_closing,
      total_actual_closing: overall.total_actual_closing,
      sum_of_deltas: overall.sum_of_deltas,
      records_included: overall.records_included,
    };
  }
}

function toYmd(date: Date | null): string | null {
  if (!date) {
    return null;
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildTotals(rows: CashReportRow[]) {
  return {
    total_cash_collected: sumNullable(rows.map(r => r.cash_collected)),
    total_expected_closing: sumNullable(rows.map(r => r.expected_closing)),
    total_actual_closing: sumNullable(rows.map(r => r.closing_balance)),
    sum_of_deltas: sumNullable(rows.map(r => r.delta)),
    records_included: rows.length,
  };
}

function formatShift(shift: any): string {
  if (!shift) {
    return '—';
  }
  if (typeof shift === 'string') {
    return shift || '—';
  }
  if (typeof shift === 'object') {
    const name = (shift as Record<string, any>)['name'];
    return name != null && String(name).trim() ? String(name) : '—';
  }
  return '—';
}

function normalizeMatchStatus(value: any): CashReportMatchStatus {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (raw === 'matched') return 'matched';
  if (raw === 'unmatched') return 'unmatched';
  if (raw === 'entry_not_done') return 'entry_not_done';
  return 'pending';
}

function toNum(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function sumNullable(values: (number | null)[]): number {
  return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
}
